import { createClient } from '@supabase/supabase-js';
import type { Database } from './database.types';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

// PKCE (standard sécurisé pour une SPA). Le vrai bug du loop n'était pas le flow mais le
// front qui redirigeait vers /login pendant le chargement (effaçant le code de l'URL avant
// l'échange) — corrigé dans AuthProvider / ProtectedRoute / AppHome.
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
  },
});

export type UserRole = 'super_admin' | 'admin' | 'client' | 'sdr';

export interface Profile {
  user_id: string;
  role: UserRole;
  client_id?: string;
  full_name?: string;
  email?: string;
  /** Métier de prospection pour les internes (role admin/sdr). Pilote le front vu :
   *  cold_call → vue SDR, gtm → vue GTM, null → vue CSM/manager. */
  prospecting_team?: 'cold_call' | 'gtm' | null;
  sales_name?: string;
}

export async function signIn(email: string, password: string) {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password
  });
  if (error) throw error;
  return data;
}

export async function signOut() {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

/**
 * Connexion SSO (OAuth). 'google' = Google, 'azure' = Microsoft (nom Supabase).
 * Après le provider, retour sur /app (l'AuthProvider récupère la session et
 * `getCurrentUser` vérifie qu'un profil existe — pas de self-signup).
 */
export async function signInWithProvider(provider: 'google' | 'azure') {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider,
    options: {
      redirectTo: `${window.location.origin}/app`,
      ...(provider === 'azure' ? { scopes: 'email openid profile' } : {}),
    },
  });
  if (error) throw error;
  return data;
}

/**
 * Première connexion / connexion sans mot de passe : Supabase envoie un code à 6 chiffres
 * (template « Magic Link » avec {{ .Token }}). shouldCreateUser=false → réservé aux comptes
 * déjà invités, jamais d'auto-inscription.
 */
export async function requestLoginCode(email: string) {
  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: { shouldCreateUser: false, emailRedirectTo: `${window.location.origin}/login/code` },
  });
  if (error) throw error;
}

/** Vérifie le code reçu par mail et ouvre la session (aucune URL en jeu → indépendant du flux PKCE). */
export async function verifyLoginCode(email: string, code: string) {
  const { data, error } = await supabase.auth.verifyOtp({ email, token: code, type: 'email' });
  if (error) throw error;
  if (!data.session) throw new Error('Session non ouverte après vérification du code.');
  return data;
}

export async function resetPassword(email: string) {
  const response = await fetch('https://hook.eu2.make.com/hoakz178evzastejmi8k0excfbuzo0sx', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ 
      email: email,
      timestamp: new Date().toISOString(),
      app_name: 'Hook Lab'
    })
  });

  if (!response.ok) {
    let errorMessage = 'Erreur lors de l\'envoi de la demande';
    try {
      const result = await response.json();
      errorMessage = result.error || errorMessage;
    } catch {
      // If JSON parsing fails, try to get plain text error
      try {
        errorMessage = await response.text() || errorMessage;
      } catch {
        // Keep default error message if both fail
      }
    }
    throw new Error(errorMessage);
  }

  // For successful responses, return a generic success object
  return { success: true, message: 'Demande envoyée avec succès' };
}

export async function getCurrentUser(): Promise<Profile | null> {
  try {
    // Check session first
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError) {
      console.error('Session error in getCurrentUser:', sessionError);
      throw sessionError;
    }
    
    if (!session?.user) {
      console.log('No session or user in getCurrentUser');
      return null;
    }

    // Get profile
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('*')
      .eq('user_id', session.user.id)
      .maybeSingle();

    if (profileError) {
      console.error('Profile error in getCurrentUser:', profileError);
      throw profileError;
    }
    
    if (!profile) {
      console.warn('No profile found for user:', session.user.id);
      return null;
    }

    return {
      ...profile,
      full_name: session.user.user_metadata?.full_name || session.user.email?.split('@')[0] || 'User',
      email: session.user.email
    };
  } catch (error) {
    console.error('Error in getCurrentUser:', error);
    throw error;
  }
}